import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { PaymentModal } from '../components/PaymentModal';
import { SOSButton } from '../components/SOSButton';
import { Footer } from '../components/Footer';
import { UserMenu } from '../components/UserMenu';
import {
  Stethoscope,
  Shield,
  Lock,
  User,
  CloudArrowUp,
  FilePdf,
  FileImage,
  File,
  Trash,
  CheckCircle,
  Warning,
  Brain,
  ArrowLeft,
  PaperPlaneTilt,
  Heart,
  ShieldCheck,
  CreditCard,
  Pulse,
  FirstAid
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Consultation type config for display
const CONSULTATION_CONFIG = {
  consultation: { title: 'Consultație Digitală Asistată', icon: Stethoscope, color: 'text-primary' },
  emergency: { title: 'Evaluare Rapidă a Urgențelor', icon: FirstAid, color: 'text-destructive' },
  second_opinion: { title: 'Second Opinion Digital', icon: Heart, color: 'text-emerald-600' },
  lab_results: { title: 'Interpretare Analize Medicale', icon: Pulse, color: 'text-blue-600' },
  imaging: { title: 'Interpretare Investigații Imagistice', icon: Brain, color: 'text-purple-600' },
  specialist: { title: 'Programare către Medic Specialist', icon: User, color: 'text-amber-600' }
};

// AI Loading Animation Component
function AILoadingScreen({ consultationType }) {
  const config = CONSULTATION_CONFIG[consultationType] || CONSULTATION_CONFIG.consultation;
  const IconComponent = config.icon;
  
  const [step, setStep] = useState(0);
  const steps = [
    'Analizez simptomele furnizate...',
    'Procesez documentele medicale...',
    'Evaluez istoricul medical...',
    'Generez recomandări personalizate...',
    'Verific siguranța recomandărilor...'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setStep(prev => (prev + 1) % steps.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-primary/95 to-primary via-primary/90">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="relative text-center text-white px-8">
        {/* Animated Icon with Pulse Rings */}
        <div className="relative mx-auto w-32 h-32 mb-8">
          {/* Pulse rings */}
          <div className="absolute inset-0 rounded-full bg-white/20 pulse-ring" />
          <div className="absolute inset-2 rounded-full bg-white/20 pulse-ring" style={{ animationDelay: '0.5s' }} />
          <div className="absolute inset-4 rounded-full bg-white/20 pulse-ring" style={{ animationDelay: '1s' }} />
          
          {/* Main icon */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-2xl">
              <IconComponent weight="duotone" className={`w-10 h-10 ${config.color}`} />
            </div>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl sm:text-3xl font-bold mb-4">
          MEDICULDIGITAL
        </h2>
        <p className="text-lg text-white/90 mb-8">
          analizează consultația dumneavoastră cu S8NTH Strategic Engine
        </p>

        {/* Progress Steps */}
        <div className="max-w-md mx-auto">
          <div className="flex items-center justify-center gap-2 mb-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-all duration-500 ${
                  i === step ? 'w-8 bg-white' : 'bg-white/40'
                }`}
              />
            ))}
          </div>
          <p className="text-white/80 text-sm animate-pulse">
            {steps[step]}
          </p>
        </div>

        {/* Loading Dots */}
        <div className="flex justify-center gap-1 mt-8">
          <span className="w-2 h-2 rounded-full bg-white loading-dot" />
          <span className="w-2 h-2 rounded-full bg-white loading-dot" />
          <span className="w-2 h-2 rounded-full bg-white loading-dot" />
        </div>

        {/* Disclaimer */}
        <p className="mt-12 text-xs text-white/60 max-w-sm mx-auto">
          Vă rugăm să așteptați. Analiza poate dura între 30 secunde și 2 minute, 
          în funcție de complexitatea cazului și numărul de documente.
        </p>
      </div>
    </div>
  );
}

export default function ConsultationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscription, refreshSubscription } = useSubscription();
  
  const consultationType = location.state?.consultationType || 'consultation';
  const config = CONSULTATION_CONFIG[consultationType] || CONSULTATION_CONFIG.consultation;
  
  const [symptoms, setSymptoms] = useState('');
  const [documents, setDocuments] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [result, setResult] = useState(null);

  // Fetch existing documents
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/documents`, {
          withCredentials: true
        });
        setDocuments(data);
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setIsLoadingDocs(false);
      }
    };
    fetchDocuments();
  }, []);

  // File upload handlers
  const handleFileUpload = async (files) => {
    const allowedExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'webp'];
    
    for (const file of files) {
      const fileName = file.name.toLowerCase();
      const lastDotIndex = fileName.lastIndexOf('.');
      const ext = lastDotIndex !== -1 ? fileName.substring(lastDotIndex + 1) : '';
      
      if (!allowedExtensions.includes(ext)) {
        toast.error(`Tipul fișierului ${file.name} nu este suportat. Folosiți PDF, PNG, JPEG sau WEBP.`);
        continue;
      }

      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      try {
        const { data } = await axios.post(`${API_URL}/api/documents/upload`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setDocuments(prev => [...prev, data]);
        setSelectedDocs(prev => [...prev, data.id]); // Auto-select newly uploaded
        toast.success(`${file.name} încărcat cu succes`);
      } catch (error) {
        const errorMsg = error.response?.data?.detail || error.message || 'Eroare necunoscută';
        toast.error(`Eroare la încărcarea ${file.name}: ${errorMsg}`);
      }
    }
    setIsUploading(false);
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${API_URL}/api/documents/${docId}`, {
        withCredentials: true
      });
      setDocuments(prev => prev.filter(d => d.id !== docId));
      setSelectedDocs(prev => prev.filter(id => id !== docId));
      toast.success('Document șters');
    } catch (error) {
      toast.error('Eroare la ștergerea documentului');
    }
  };

  const toggleSelect = (docId) => {
    setSelectedDocs(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  const getFileIcon = (contentType) => {
    if (contentType === 'application/pdf') {
      return <FilePdf weight="duotone" className="w-8 h-8 text-red-500" />;
    }
    if (contentType?.startsWith('image/')) {
      return <FileImage weight="duotone" className="w-8 h-8 text-blue-500" />;
    }
    return <File weight="duotone" className="w-8 h-8 text-gray-500" />;
  };

  // Submit consultation
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!symptoms.trim()) {
      toast.error('Vă rugăm descrieți simptomele');
      return;
    }

    // Check payment status
    if (!subscription?.can_consult) {
      setShowPaymentModal(true);
      return;
    }

    setIsAnalyzing(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/consultation`,
        {
          symptoms: symptoms.trim(),
          document_ids: selectedDocs,
          consultation_type: consultationType
        },
        { 
          withCredentials: true,
          timeout: 180000
        }
      );

      setResult(response.data);
      refreshSubscription();

      if (response.data.is_emergency) {
        setTimeout(() => {
          toast.error('ATENȚIE: Situație de urgență detectată! Apelați 112!', {
            duration: 10000,
          });
        }, 500);
      }

    } catch (error) {
      console.error('Consultation error:', error);
      const statusCode = error.response?.status;
      
      if (statusCode === 402) {
        setShowPaymentModal(true);
      } else {
        const errorMsg = error.response?.data?.detail || error.message || 'Eroare la conectare';
        toast.error(`Eroare: ${errorMsg}`);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Show loading screen during AI analysis
  if (isAnalyzing) {
    return <AILoadingScreen consultationType={consultationType} />;
  }

  return (
    <div className="min-h-screen medical-gradient flex flex-col" data-testid="consultation-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-border/50 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2 text-primary">
              <Stethoscope weight="fill" className="w-7 h-7" />
              <div className="hidden sm:block">
                <span className="text-lg font-bold">MEDICULDIGITAL</span>
                <span className="text-xs block text-muted-foreground">Platforma Medicală S8NTH</span>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="hidden md:flex items-center gap-3">
              <span className="trust-badge">
                <Lock weight="fill" className="w-3.5 h-3.5" />
                Date medicale criptate
              </span>
              <span className="trust-badge">
                <Shield weight="fill" className="w-3.5 h-3.5" />
                GDPR
              </span>
            </div>

            {/* User Menu */}
            <UserMenu />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {!result ? (
            // Consultation Form
            <div className="space-y-6 animate-in fade-in duration-500">
              {/* Back Button & Type Header */}
              <div className="flex items-center gap-4">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => navigate('/dashboard')}
                  className="shrink-0"
                  data-testid="back-button"
                >
                  <ArrowLeft weight="bold" className="w-5 h-5" />
                </Button>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-xl bg-primary/10`}>
                    <config.icon weight="duotone" className={`w-6 h-6 ${config.color}`} />
                  </div>
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-foreground">
                      {config.title}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      Completați informațiile pentru consultație
                    </p>
                  </div>
                </div>
              </div>

              {/* Subscription Status */}
              {subscription && !subscription.can_consult && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Lock weight="duotone" className="w-5 h-5 text-amber-600" />
                    <span className="text-sm text-amber-800">
                      Nu aveți consultații disponibile. Achiziționați un pachet pentru a continua.
                    </span>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => setShowPaymentModal(true)}
                    className="gap-2"
                    data-testid="buy-access-btn"
                  >
                    <CreditCard weight="bold" className="w-4 h-4" />
                    Cumpără Acces
                  </Button>
                </div>
              )}

              {subscription && subscription.can_consult && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2">
                  <ShieldCheck weight="duotone" className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm text-emerald-800">
                    {subscription.is_subscribed 
                      ? 'Abonament activ - consultații nelimitate' 
                      : `${subscription.consultations_remaining} consultație(i) disponibilă(e)`}
                  </span>
                </div>
              )}

              {/* Emergency Warning */}
              {consultationType === 'emergency' && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Warning weight="fill" className="w-6 h-6 text-destructive flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-destructive">
                      <p className="font-semibold mb-1">ATENȚIE: Evaluare Urgențe</p>
                      <p>
                        Dacă simptomele sugerează o urgență medicală majoră (durere toracică severă, 
                        dificultăți importante de respirație, pierderea stării de conștiență, 
                        semne de accident vascular cerebral etc.), apelați imediat serviciul de urgență 112.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Symptoms Form */}
              <div className="premium-card p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Descrieți simptomele / situația medicală
                  </label>
                  <Textarea
                    placeholder="Descrieți în detaliu simptomele, durata acestora, intensitatea și orice informație relevantă..."
                    value={symptoms}
                    onChange={(e) => setSymptoms(e.target.value)}
                    className="min-h-[150px] resize-none"
                    data-testid="symptoms-textarea"
                  />
                </div>

                {/* Document Upload Zone */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-foreground">
                    Adăugați documente medicale (opțional)
                  </h3>
                  
                  <div
                    className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                      isDragging 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    data-testid="dropzone"
                  >
                    <input
                      type="file"
                      id="file-upload"
                      className="hidden"
                      accept=".pdf,.png,.jpg,.jpeg,.webp"
                      multiple
                      onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                      data-testid="file-input"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <CloudArrowUp weight="duotone" className={`w-12 h-12 mx-auto mb-3 ${
                        isDragging ? 'text-primary' : 'text-muted-foreground'
                      }`} />
                      <p className="text-foreground font-medium mb-1">
                        {isUploading ? 'Se încarcă...' : 'Trageți documentele aici'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        sau click pentru a selecta (PDF, PNG, JPEG, WEBP)
                      </p>
                    </label>
                  </div>

                  {/* Document List */}
                  {isLoadingDocs ? (
                    <div className="space-y-2">
                      {[1, 2].map(i => (
                        <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />
                      ))}
                    </div>
                  ) : documents.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        Selectați documentele pentru consultație:
                      </p>
                      {documents.map((doc) => (
                        <div
                          key={doc.id}
                          className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
                            selectedDocs.includes(doc.id)
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/50'
                          }`}
                          onClick={() => toggleSelect(doc.id)}
                          data-testid={`doc-${doc.id}`}
                        >
                          {getFileIcon(doc.content_type)}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground truncate">{doc.filename}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(doc.uploaded_at).toLocaleDateString('ro-RO')}
                            </p>
                          </div>
                          {selectedDocs.includes(doc.id) && (
                            <CheckCircle weight="fill" className="w-5 h-5 text-primary" />
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(doc.id);
                            }}
                          >
                            <Trash weight="bold" className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedDocs.length > 0 && (
                    <p className="text-sm text-primary font-medium">
                      {selectedDocs.length} document(e) selectat(e) pentru consultație
                    </p>
                  )}
                </div>

                {/* Disclaimer */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <Warning weight="fill" className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800">
                      <strong>IMPORTANT:</strong> Utilizarea serviciilor MEDICULDIGITAL NU exclude și 
                      NU înlocuiește consultarea unui medic uman. Pentru urgențe medicale, apelați 112.
                    </p>
                  </div>
                </div>

                {/* Submit Button */}
                <Button 
                  onClick={handleSubmit}
                  className="w-full h-14 text-base gap-2"
                  disabled={!symptoms.trim()}
                  data-testid="submit-consultation-btn"
                >
                  <PaperPlaneTilt weight="fill" className="w-5 h-5" />
                  Solicită Consultație Digitală Asistată {!subscription?.can_consult && '(5€)'}
                </Button>
              </div>
            </div>
          ) : (
            // Results View
            <div className="space-y-6 animate-in fade-in duration-500">
              <div className="flex items-center gap-4">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => navigate('/dashboard')}
                  className="shrink-0"
                >
                  <ArrowLeft weight="bold" className="w-5 h-5" />
                </Button>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-foreground">
                    Rezultatul Consultației
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {new Date(result.created_at).toLocaleString('ro-RO')}
                  </p>
                </div>
              </div>

              {/* Emergency Alert */}
              {result.is_emergency && (
                <div className="bg-destructive/10 border-2 border-destructive rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Warning weight="fill" className="w-8 h-8 text-destructive" />
                    <h2 className="text-xl font-bold text-destructive">
                      URGENȚĂ MEDICALĂ DETECTATĂ!
                    </h2>
                  </div>
                  <p className="text-destructive font-medium">
                    Sistemul a detectat simptome care pot indica o urgență medicală.
                    Vă recomandăm să apelați 112 imediat sau să vă prezentați la cea mai 
                    apropiată Unitate de Primiri Urgențe.
                  </p>
                </div>
              )}

              {/* Symptoms */}
              <div className="premium-card p-6">
                <div className="flex items-center gap-2 mb-3">
                  <User weight="duotone" className="w-5 h-5 text-muted-foreground" />
                  <h3 className="font-semibold text-foreground">Simptome Raportate</h3>
                </div>
                <p className="text-foreground bg-muted/50 rounded-lg p-4">
                  {result.symptoms}
                </p>
              </div>

              {/* Diagnosis */}
              <div className="premium-card p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Brain weight="duotone" className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-foreground">Analiză și Recomandări</h3>
                </div>
                <div className="prose prose-sm max-w-none text-foreground whitespace-pre-wrap">
                  {result.diagnosis}
                </div>
              </div>

              {/* Safety Notes */}
              {result.safety_notes && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck weight="duotone" className="w-5 h-5 text-emerald-600" />
                    <h3 className="font-semibold text-emerald-800">Verificare Siguranță</h3>
                  </div>
                  <p className="text-sm text-emerald-700 whitespace-pre-wrap">
                    {result.safety_notes}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => {
                    setResult(null);
                    setSymptoms('');
                    setSelectedDocs([]);
                  }}
                  variant="outline"
                  className="flex-1 h-12"
                >
                  Consultație Nouă
                </Button>
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="flex-1 h-12"
                >
                  Înapoi la Dashboard
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <Footer />

      {/* SOS Button */}
      <SOSButton />

      {/* Payment Modal */}
      <PaymentModal 
        open={showPaymentModal} 
        onOpenChange={setShowPaymentModal}
        onPaymentComplete={() => refreshSubscription()}
      />
    </div>
  );
}
